import React from 'react';

const SentimentSummary = ({ data }) => {
  const total = data.length;
  const positive = data.filter(review => review.sentiment === 'Positive').length;
  const negative = data.filter(review => review.sentiment === 'Negative').length;
  const neutral = data.filter(review => review.sentiment === 'Neutral' || (!review.sentiment === 'Positive' && !review.sentiment === 'Negative')).length;
  
  const positivePercentage = total > 0 ? Math.round((positive / total) * 100) : 0;
  const negativePercentage = total > 0 ? Math.round((negative / total) * 100) : 0;
  const neutralPercentage = total > 0 ? 100 - positivePercentage - negativePercentage : 0;
  
  // Calculate the trend (comparing to previous period)
  // Using plain JavaScript Date operations
  const now = new Date();
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(now.getMonth() - 3);
  
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(now.getMonth() - 6);
  
  const recentReviews = data.filter(review => {
    const reviewDate = new Date(review.date);
    return reviewDate >= threeMonthsAgo;
  });
  
  const previousReviews = data.filter(review => {
    const reviewDate = new Date(review.date);
    return reviewDate >= sixMonthsAgo && reviewDate < threeMonthsAgo;
  });
  
  const recentPositive = recentReviews.filter(review => review.sentiment === 'Positive').length;
  const previousPositive = previousReviews.filter(review => review.sentiment === 'Positive').length;
  
  const positiveTrend = previousPositive > 0 
    ? Math.round(((recentPositive - previousPositive) / previousPositive) * 100) 
    : 0;
    
  // SVG Donut Chart
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  
  // Calculate stroke dasharray values for each segment
  const positiveDash = (positivePercentage / 100) * circumference;
  const negativeDash = (negativePercentage / 100) * circumference;
  const neutralDash = (neutralPercentage / 100) * circumference;
  
  // Position each segment by offsetting it
  const positiveOffset = 0;
  const negativeOffset = positiveDash;
  const neutralOffset = positiveDash + negativeDash;

  // Calculate average sentiment score (scale: -100 to 100)
  const sentimentScore = total > 0 
    ? Math.round((positivePercentage - negativePercentage)) 
    : 0;
  
  // Determine color and icon based on sentiment score
  let scoreColor = "text-gray-500";
  let scoreIcon = null;
  
  if (sentimentScore > 50) {
    scoreColor = "text-emerald-500";
    scoreIcon = (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-7.536 5.879a1 1 0 001.415 0 3 3 0 014.242 0 1 1 0 001.415-1.415 5 5 0 00-7.072 0 1 1 0 000 1.415z" clipRule="evenodd" />
      </svg>
    );
  } else if (sentimentScore > 0) {
    scoreColor = "text-green-500";
    scoreIcon = (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-.464 5.535a1 1 0 10-1.415-1.414 3 3 0 01-4.242 0 1 1 0 00-1.415 1.414 5 5 0 007.072 0z" clipRule="evenodd" />
      </svg>
    );
  } else if (sentimentScore === 0) {
    scoreColor = "text-gray-500";
    scoreIcon = (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-7.536 4.464a1 1 0 001.414 0 3 3 0 014.242 0 1 1 0 001.414-1.414 5 5 0 00-7.07 0 1 1 0 000 1.414z" clipRule="evenodd" />
      </svg>
    );
  } else {
    scoreColor = "text-rose-500";
    scoreIcon = (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-7.536 5.879a1 1 0 001.415 0 3 3 0 014.242 0 1 1 0 001.415-1.415 5 5 0 00-7.072 0 1 1 0 000 1.415z" clipRule="evenodd" />
      </svg>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-50">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-700">Sentiment Analysis</h2>
          <p className="text-sm text-gray-400">Review sentiment distribution</p>
        </div>
        {positiveTrend !== 0 && (
          <div className={`py-1 px-3 rounded-full ${positiveTrend > 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'} flex items-center text-sm font-medium`}>
            {positiveTrend > 0 ? (
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            ) : (
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
              </svg>
            )}
            <span>{Math.abs(positiveTrend)}% from previous</span>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Left side: Donut chart */}
        <div className="md:col-span-5 flex flex-col justify-center items-center">
          <div className="relative w-48 h-48">
            <svg viewBox="0 0 200 200" className="transform -rotate-90">
              {/* Donut chart background */}
              <circle 
                cx="100" 
                cy="100" 
                r={radius} 
                fill="none" 
                stroke="#f8fafc" 
                strokeWidth="24"
              />
              
              {/* Positive segment */}
              <circle 
                cx="100" 
                cy="100" 
                r={radius} 
                fill="none" 
                stroke="#93fdc4" 
                strokeWidth="24"
                strokeDasharray={`${positiveDash} ${circumference - positiveDash}`} 
                strokeDashoffset={positiveOffset} 
                strokeLinecap="round"
              />
              
              {/* Negative segment */}
              <circle 
                cx="100" 
                cy="100" 
                r={radius} 
                fill="none" 
                stroke="#FCA5A5" 
                strokeWidth="24"
                strokeDasharray={`${negativeDash} ${circumference - negativeDash}`} 
                strokeDashoffset={-negativeOffset} 
                strokeLinecap="round"
              />
              
              {/* Neutral segment */}
              {neutralPercentage > 0 && (
                <circle 
                  cx="100" 
                  cy="100" 
                  r={radius} 
                  fill="none" 
                  stroke="#E5E7EB" 
                  strokeWidth="24"
                  strokeDasharray={`${neutralDash} ${circumference - neutralDash}`} 
                  strokeDashoffset={-neutralOffset} 
                  strokeLinecap="round"
                />
              )}
            </svg>
            
            {/* Total in the center */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-700">{total}</div>
                <div className="text-xs text-gray-400">Total Reviews</div>
              </div>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap justify-center mt-4">
            <div className="flex items-center mx-3 mb-2">
              <div className="w-3 h-3 rounded-full bg-green-300 mr-2"></div>
              <span className="text-sm text-gray-600">Positive ({positivePercentage}%)</span>
            </div>
            <div className="flex items-center mx-3 mb-2">
              <div className="w-3 h-3 rounded-full bg-red-300 mr-2"></div>
              <span className="text-sm text-gray-600">Negative ({negativePercentage}%)</span>
            </div>
            {neutralPercentage > 0 && (
              <div className="flex items-center mx-3 mb-2">
                <div className="w-3 h-3 rounded-full bg-gray-300 mr-2"></div>
                <span className="text-sm text-gray-600">Neutral ({neutralPercentage}%)</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Right side: Metrics */}
        <div className="md:col-span-7 grid grid-cols-1 gap-4">
          {/* Summary score card */}
          <div className="p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Sentiment Score</h3>
                <div className="text-3xl font-bold mt-1 flex items-baseline">
                  <span className={scoreColor}>{sentimentScore}</span>
                  <span className="text-gray-400 text-sm ml-1">/ 100</span>
                </div>
              </div>
              <div className={`${scoreColor} text-center`}>
                {scoreIcon}
              </div>
            </div>
            
            {/* Sentiment meter */}
            <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full ${
                  sentimentScore > 50 ? 'bg-emerald-500' : 
                  sentimentScore > 0 ? 'bg-green-500' : 
                  sentimentScore === 0 ? 'bg-gray-500' : 
                  'bg-rose-500'
                }`} 
                style={{ width: `${Math.max(0, Math.min(100, sentimentScore + 100) / 2)}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Negative</span>
              <span>Neutral</span>
              <span>Positive</span>
            </div>
          </div>
          
          {/* Metrics cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border border-gray-100 rounded-xl bg-gradient-to-br from-blue-50 to-white">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Positive</h3>
              <div className="text-3xl font-bold text-green-500">{positive}</div>
              <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-green-400" style={{ width: `${positivePercentage}%` }}></div>
              </div>
              <div className="text-right text-sm text-gray-500 mt-1">{positivePercentage}%</div>
            </div>
            
            <div className="p-4 border border-gray-100 rounded-xl bg-gradient-to-br from-red-50 to-white">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Negative</h3>
              <div className="text-3xl font-bold text-red-400">{negative}</div>
              <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-red-300" style={{ width: `${negativePercentage}%` }}></div>
              </div>
              <div className="text-right text-sm text-gray-500 mt-1">{negativePercentage}%</div>
            </div>
            
            <div className="p-4 border border-gray-100 rounded-xl bg-gradient-to-br from-gray-50 to-white">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Neutral</h3>
              <div className="text-3xl font-bold text-gray-500">{neutral}</div>
              <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-gray-400" style={{ width: `${neutralPercentage}%` }}></div>
              </div>
              <div className="text-right text-sm text-gray-500 mt-1">{neutralPercentage}%</div>
            </div>
          </div>
          
          {/* Trend box */}
          {positiveTrend !== 0 && (
            <div className="p-4 border border-gray-100 rounded-xl bg-gradient-to-br from-slate-50 to-white">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Trend Analysis</h3>
              <div className="flex items-center">
                <div className={`text-xl font-bold ${positiveTrend > 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {positiveTrend > 0 ? '+' : ''}{positiveTrend}%
                </div>
                <div className="text-sm text-gray-500 ml-2">in positive sentiment</div>
                <div className="ml-auto text-sm text-gray-500">
                  vs previous 3 months
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SentimentSummary;